import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getTemplates, deleteTemplate, assignMembersToTemplate } from 'team/apis/teamTemplateApi';
import TeamTemplateNavbar from 'team/components/templates/TeamTemplateNavbar';
import TeamTemplateList from 'team/components/templates/TeamTemplateList';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import TeamTemplateAssignMembersModal from 'team/components/templates/TeamTemplateAssignMembersModal';

const TeamTemplatesContainer = ({ user, team }) => {
    const navigate = useNavigate();
    const teamId = team?._id || team?.id;

    const [templates, setTemplates] = useState([]);
    const [templatesCount, setTemplatesCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const fetchTemplates = async () => {
        if (!teamId) {
            console.warn('[TeamTemplatesContainer] No teamId available, cannot fetch templates');
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            console.log('[TeamTemplatesContainer] Fetching templates for teamId:', teamId);
            const response = await getTemplates(teamId);
            console.log('[TeamTemplatesContainer] Templates API response:', {
                success: response?.success,
                templatesCount: response?.templates?.length,
                totalTemplates: response?.pagination?.totalTemplates,
                templates: response?.templates
            });

            if (response && response.success) {
                const fetchedTemplates = response.templates || [];
                console.log('[TeamTemplatesContainer] Setting templates:', fetchedTemplates.length);
                setTemplates(fetchedTemplates);
                setTemplatesCount(response.pagination?.totalTemplates || fetchedTemplates.length);
            } else {
                console.error('[TeamTemplatesContainer] API response was not successful:', response);
                setTemplates([]);
                setTemplatesCount(0);
            }
        } catch (error) {
            console.error('[TeamTemplatesContainer] Error fetching templates:', error);
            console.error('[TeamTemplatesContainer] Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            showSnackbar(error.message || 'Failed to load templates', 'error');
            setTemplates([]);
            setTemplatesCount(0);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        console.log('[TeamTemplatesContainer] useEffect triggered:', {
            teamId,
            team: team?._id || team?.id,
            teamData: team,
            hasTeam: !!team
        });

        // Only fetch if we have a valid teamId
        if (teamId) {
            fetchTemplates();
        } else {
            console.warn('[TeamTemplatesContainer] No teamId available, waiting for team data...');
            setIsLoading(false);
        }
    }, [teamId, team]);

    const handleCreateTemplate = (type, card) => {
        // Navigate to create template page (using team route)
        // If card is provided, pass it via state
        // Also pass templatesCount to calculate the next template number
        const nextTemplateNumber = templatesCount + 1;
        if (type === 'from-card' && card) {
            navigate(`/team/template/create`, { state: { fromCard: card, templatesCount: templatesCount } });
        } else {
            navigate(`/team/template/create`, { state: { templatesCount: templatesCount } });
        }
    };

    const handleEdit = (template) => {
        // Use team route: /team/template/:templateId
        navigate(`/team/template/${template._id}`);
    };

    const handleDelete = async (templateId) => {
        if (!templateId || !teamId) {
            showSnackbar('Template ID and Team ID are required', 'error');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this template?')) {
            return;
        }

        try {
            await deleteTemplate(teamId, templateId);
            showSnackbar('Template deleted successfully', 'success');
            fetchTemplates();
        } catch (error) {
            console.error('Error deleting template:', error);
            showSnackbar(error.message || 'Failed to delete template', 'error');
        }
    };

    const handleAssignMembers = (template) => {
        setSelectedTemplate(template);
        setAssignModalOpen(true);
    };

    const handleAssignMembersSave = async (data) => {
        try {
            const { templateId, assignMemberIds, unassignMemberIds, setAsDefault } = data;

            if (!templateId || !teamId) {
                showSnackbar('Template ID and Team ID are required', 'error');
                return;
            }

            await assignMembersToTemplate(teamId, templateId, {
                assignMemberIds,
                unassignMemberIds,
                setAsDefault
            });

            // Show appropriate message based on action performed
            const assignCount = assignMemberIds?.length || 0;
            const unassignCount = unassignMemberIds?.length || 0;

            let message = '';
            if (unassignCount > 0 && assignCount > 0) {
                message = `${assignCount} member${assignCount !== 1 ? 's' : ''} added, ${unassignCount} member${unassignCount !== 1 ? 's' : ''} removed`;
            } else if (unassignCount > 0) {
                message = `${unassignCount} member${unassignCount !== 1 ? 's' : ''} removed from template`;
            } else if (assignCount > 0) {
                message = `${assignCount} member${assignCount !== 1 ? 's' : ''} assigned to template`;
            } else {
                message = 'Template updated successfully';
            }

            showSnackbar(message, 'success');
            setAssignModalOpen(false);
            setSelectedTemplate(null);
            fetchTemplates();
        } catch (error) {
            console.error('Error updating template members:', error);
            showSnackbar(error.message || 'Failed to update template members', 'error');
        }
    };

    return (
        <Box sx={{ width: '100%', padding: 2 }}>
            <TeamTemplateNavbar
                templatesCount={templatesCount}
                onCreateTemplate={handleCreateTemplate}
                team={team}
            />

            <TeamTemplateList
                templates={templates}
                isLoading={isLoading}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onAssignMembers={handleAssignMembers}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
            />

            {selectedTemplate && (
                <TeamTemplateAssignMembersModal
                    open={assignModalOpen}
                    onClose={() => {
                        setAssignModalOpen(false);
                        setSelectedTemplate(null);
                    }}
                    template={selectedTemplate}
                    teamId={teamId}
                    onSave={handleAssignMembersSave}
                />
            )}

            <TeamNotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                handleClose={handleCloseSnackbar}
            />
        </Box>
    );
};

export default TeamTemplatesContainer;
